import React, { useState, useEffect } from "react";
import { FormBuilderProps, FormElement } from "./FormBuilder.types";
import FormFieldRenderer from "./FormFieldRenderer";
import SuccessMessage from "./SuccessMessage";
import { sanitizeHtml } from "../../utils/sanitize";
import { useAddressFromCep } from "../../hooks/useAddressFromCep";
import { useAntiBot, ANTIBOT_ACTIONS } from "../../hooks/useAntiBot";
import { useFormValidation } from "./hooks/useFormValidation";
import { useFormSubmit } from "./hooks/useFormSubmit";
import { Button } from "../ButtonV2/Button";
import {
  Form as FormStyled,
  FormSpanError,
  FormSeparator,
  PrivacyPolicy,
} from "./FormBuilder.styles";
import { Title } from "../Title/Title";
import { Text } from "../Text/Text";
import { getUnitDisplayName } from "../../utils/unit";
import { AntiBotNotice } from "../AntiBotNotice";
import { getOrCreateVisitorId } from "../../utils/visitorId";
import { useFormUnits } from "../../hooks/useFormUnits";
import { SUBMITTING_LABEL } from "../../constants/form";

export const FormBuilder = ({
  form,
  submitUrl,
  buttonLabel,
  palette,
  formStyles,
  privacyPolicy,
  afterRedirect,
  dynamicStructure,
  gtagLabel,
  onSubmitStart,
  onSubmitSuccess,
  onSubmitError,
  showSuccessMessage,
  onBackToForm,
  product,
  category,
  channel,
  siteId,
  teamId,
  moduleId,
  accountId,
  contentEntryId,
  formId,
  antiBot,
  dynamicFields,
  useGlobalQueryParams,
}: FormBuilderProps) => {
  /**
   * As unidades que o formulário oferece: as do registro, senão as da região
   * escolhida pelo visitante, senão as do site inteiro.
   */
  const { units: formUnits } = useFormUnits({
    channelUnits: dynamicStructure?.channel?.units,
    unitGroupingMode: dynamicStructure?.channel?.unitGroupingMode,
    unitGroups: dynamicStructure?.channel?.unitGroups,
    entry: dynamicStructure?.item,
    useGlobalQueryParams,
  });

  const [internalFormSent, setInternalFormSent] = useState(false);
  const formSent = showSuccessMessage ?? internalFormSent;

  const [originUrl, setOriginUrl] = useState("");
  const [cepState, setCepState] = useState("");
  const [visitorId, setVisitorId] = useState<string | null>(null);

  const { validationErrors, validateFields, handleFieldChange } =
    useFormValidation({ form });

  const { execute: executeAntiBot, requestScript: requestAntiBotScript } = useAntiBot(antiBot);

  const { isSubmitting, serverError, submitForm } = useFormSubmit({
    submitUrl,
    afterRedirect,
    dynamicFields,
    dynamicStructure,
    formId,
    getAntiBotToken: () => executeAntiBot(ANTIBOT_ACTIONS.FORM_SUBMIT),
    onSuccess: (data) => {
      setInternalFormSent(true);
      onSubmitSuccess?.(data);
    },
    onError: onSubmitError,
    onStart: onSubmitStart,
  });

  const { city, state } = useAddressFromCep(cepState);

  useEffect(() => {
    if (typeof window !== "undefined") {
      setOriginUrl(window.location.href);
      setVisitorId(getOrCreateVisitorId());
    }
  }, []);

  const isHiddenUnitField = (element: FormElement) =>
    element.props.attribute_name === 'unit' && element.props.hidden === true;

  const resolveDynamicFieldValue = (element: FormElement): string => {
    if (isHiddenUnitField(element)) {
      if (formUnits.length === 1) return formUnits[0].id;

      return '';
    }

    return element.props.value || '';
  };

  const getUnitSelectOptions = (element: FormElement) => {
    if (isHiddenUnitField(element) && formUnits.length > 1) {
      return formUnits.map((u: any) => ({
        value: u.id,
        label: getUnitDisplayName({ unit: u }),
      }));
    }

    return null;
  };

  const renderLayout = (element: FormElement) => {
    const { type, props } = element;
    const key = `layout-${element.id}`;

    if (type === 'text') {
      switch (props.variant) {
        case 'title':
          return (
            <Title key={key} style={{ color: formStyles?.textColor }}>
              {props.value}
            </Title>
          );
        case 'paragraph':
          return (
            <Text
              as="div"
              key={key}
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(props.value) }}
              style={{ color: formStyles?.textColor }}
            ></Text>
          );
        default:
          return (
            <p key={key} style={{ color: formStyles?.textColor }}>
              {props.value}
            </p>
          );
      }
    }

    if (type === 'privacy-policy') {
      return (
        <>
          <FormSeparator />
          <PrivacyPolicy
            key={key}
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(props.text || props.value || '') }}
            className="rich-text-reset-html"
            style={{ color: formStyles?.textColor }}
          />
          <AntiBotNotice antiBot={antiBot} color={formStyles?.textColor} />
        </>
      );
    }

    return null;
  };

  const renderAction = (element: FormElement) => {
    const { type, props } = element;
    const key = `action-${element.id}`;

    if (type === 'button' && props.buttonType === 'submit') {
      return (
        <Button
          key={key}
          type="submit"
          size="full"
          palette={palette}
          className="styleguide"
          disabled={isSubmitting}
          loading={isSubmitting}
          aria-label={props.label || 'Enviar'}
        >
          {isSubmitting ? SUBMITTING_LABEL : props.label || 'Enviar'}
        </Button>
      );
    }
    return null;
  };

  const renderElement = (element: FormElement) => {
    switch (element.category) {
      case 'layout':
        return renderLayout(element);
      case 'field':
        const resolvedValue = resolveDynamicFieldValue(element);
        const unitSelectOptions = getUnitSelectOptions(element);

        return (
          <FormFieldRenderer
            key={element.id}
            element={element}
            validationErrors={validationErrors}
            handleFieldChange={handleFieldChange}
            cepState={cepState}
            setCepState={setCepState}
            stateValue={state}
            city={city}
            formStyles={formStyles}
            privacyPolicy={privacyPolicy || ''}
            dynamicValue={resolvedValue}
            unitSelectOptions={unitSelectOptions}
          />
        );
      case 'action':
        return renderAction(element);
      default:
        return null;
    }
  };

  if (!form) {
    return (
      <p style={{ textAlign: 'center', color: formStyles?.textColor }}>
        Nenhum formulário encontrado.
      </p>
    );
  }

  const sortedElements = [...(form.elements || [])].sort(
    (a, b) => a.orderPosition - b.orderPosition
  );
  const feedbackElements = sortedElements.filter(
    (el) => el.category === 'feedback'
  );
  const formElements = sortedElements.filter(
    (el) => el.category !== 'feedback'
  );

  if (formSent) {
    return (
      <SuccessMessage
        feedbackElements={feedbackElements}
        onBackToForm={() => {
          setInternalFormSent(false);
          onBackToForm?.();
        }}
        palette={palette}
      />
    );
  }

  const hasSubmitButton = formElements.some(
    (el) =>
      el.category === 'action' &&
      el.type === 'button' &&
      el.props.buttonType === 'submit'
  );

  return (
    <FormStyled
      onSubmit={(e) =>
        submitForm(e, validateFields, originUrl, { city, state })
      }
      onFocus={requestAntiBotScript}
      noValidate
      data-gtag-action="Formulário Enviado"
      data-gtag-category="Formulário"
      data-gtag-label={gtagLabel}
    >
      {visitorId && <input type="hidden" name="af_visitor_id" value={visitorId} />}
      <input type="hidden" name="origin_url" value={originUrl} />
      {product && <input type="hidden" name="product" value={product} />}
      {category && <input type="hidden" name="category" value={category} />}
      {siteId && <input type="hidden" name="siteId" value={siteId} />}
      {teamId && <input type="hidden" name="teamId" value={teamId} />}
      {moduleId && <input type="hidden" name="moduleId" value={moduleId} />}
      {accountId && <input type="hidden" name="accountId" value={accountId} />}
      {contentEntryId && <input type="hidden" name="content_entry_id" value={contentEntryId} />}
      <input type="hidden" name="channel" value={channel || 'Site'} />
      {dynamicStructure?.selectedHighlightLabel && (
        <input type="hidden" name="selected_highlight" value={dynamicStructure.selectedHighlightLabel} />
      )}
      {formElements.map(renderElement)}
      {serverError && (
        <FormSpanError style={{ textAlign: "center" }}>
          Ocorreu um erro no servidor. Tente novamente mais tarde.
        </FormSpanError>
      )}
      {!hasSubmitButton && (
        <Button
          size="full"
          type="submit"
          disabled={isSubmitting}
          loading={isSubmitting}
          palette={palette}
          className="styleguide"
          aria-label={buttonLabel || 'Enviar'}
        >
          {isSubmitting ? SUBMITTING_LABEL : buttonLabel || "Enviar"}
        </Button>
      )}
    </FormStyled>
  );
};
